import React, { useState, useEffect } from 'react';
import { 
  Container, Box, Typography, Paper, 
  CircularProgress, Button, Breadcrumbs, Link,
  Stack, Chip, Avatar, Grid, Divider, 
  Tabs, Tab, List, ListItem, ListItemText, Rating, Alert
} from '@mui/material';
import { 
  Person, Edit, ArrowBack, 
  School, FamilyRestroom, Badge 
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';

const UserDetailsPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { userId } = useParams();
  
  const [userData, setUserData] = useState(null);
  const [permData, setPermData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsData, setRatingsData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const user = await fetchJson(`/api/users/${userId}`);
        setUserData(user);
        // Fetch permissions/effective overrides
        const perms = await fetchJson(`/api/users/${userId}/permissions`);
        setPermData(perms);
      } catch (error) {
        enqueueSnackbar(t('users.errorFetch'), { variant: 'error' });
        console.error('Failed to fetch user details:', error);
        navigate('/school-admin/user-list');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userId, t, enqueueSnackbar, navigate]);

  const isStaffEvaluationEligible = (() => {
    const rn = String(userData?.role?.name || '').trim().toLowerCase();
    if (!rn) return true;
    return !['student', 'parent', 'systemadmin'].includes(rn);
  })();

  useEffect(() => {
    let cancelled = false;
    if (!userId || !isStaffEvaluationEligible) return;
    (async () => {
      try {
        setRatingsLoading(true);
        const resp = await fetchJson(`/api/staff-management/evaluations/ratings-summary/by-user/${userId}`);
        if (!cancelled) setRatingsData(resp);
      } catch (_) {
        if (!cancelled) setRatingsData(null);
      } finally {
        if (!cancelled) setRatingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isStaffEvaluationEligible]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getRoleIcon = () => {
    const roleName = (userData?.role?.name || '').toLowerCase();
    if (roleName.includes('student')) return <School />;
    if (roleName.includes('parent')) return <FamilyRestroom />;
    return <Badge />;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="/school-admin/user-list">
          {t('users.title')}
        </Link>
        <Typography color="text.primary">
          {userData?.name || 'User Details'}
        </Typography>
      </Breadcrumbs>
      
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('users.userDetails')}
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Edit />}
          onClick={() => navigate(`/school-admin/users/${userId}/edit`)}
        >
          {t('common.edit')}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Paper sx={{ p: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar 
                  src={userData.avatarUrl} 
                  sx={{ width: 120, height: 120, mb: 2 }}
                >
                  {userData.name.charAt(0)}
                </Avatar>
                
                <Typography variant="h5" gutterBottom>
                  {userData.name}
                </Typography>
                
                <Chip 
                  icon={getRoleIcon()}
                  label={userData.role?.name} 
                  color="primary"
                  sx={{ mb: 1 }}
                />
                
                <Chip 
                  label={userData.status} 
                  color={getStatusColor(userData.status)}
                  sx={{ mb: 2 }}
                />
                
                <Button 
                  variant="outlined" 
                  startIcon={<ArrowBack />}
                  onClick={() => navigate('/school-admin/user-list')}
                  fullWidth
                >
                  {t('common.backToList')}
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab label={t('users.tabs.profile')} />
                <Tab label={t('users.tabs.activity')} />
                <Tab label={t('users.tabs.permissions')} />
                {isStaffEvaluationEligible && <Tab label="Staff Evaluation" />}
              </Tabs>
              
              {tabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <ListItem>
                      <ListItemText 
                        primary={t('users.details.email')} 
                        secondary={userData.email} 
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary={t('users.details.phone')} 
                        secondary={userData.phone || '-'} 
                      />
                    </ListItem>
                    
                    {/* Tier removed: staff-only roles */}
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <ListItem>
                      <ListItemText 
                        primary={t('users.details.status')} 
                        secondary={userData.status} 
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary={t('users.details.created')} 
                        secondary={new Date(userData.createdAt).toLocaleDateString()} 
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary={t('users.details.lastActive')} 
                        secondary={
                          userData.lastActive 
                            ? new Date(userData.lastActive).toLocaleString() 
                            : t('users.details.neverActive')
                        } 
                      />
                    </ListItem>
                  </Grid>
                </Grid>
              )}
              
              {tabValue === 1 && (
                <Box>
                  <Typography variant="body1" color="text.secondary">
                    {t('users.activity.comingSoon')}
                  </Typography>
                </Box>
              )}

              {tabValue === 3 && isStaffEvaluationEligible && (
                <Box>
                  {ratingsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={44} />
                    </Box>
                  ) : (ratingsData && ratingsData.eligible === false) ? (
                    <Alert severity="info">This user is not eligible for staff evaluations.</Alert>
                  ) : (
                    <>
                      <Typography variant="h6" gutterBottom>Rating Summary</Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={4}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="overline" color="text.secondary">Average Rating</Typography>
                            <Typography variant="h4" fontWeight={800}>
                              {ratingsData?.summary?.averageRating != null ? Number(ratingsData.summary.averageRating).toFixed(1) : '—'}
                            </Typography>
                            <Rating value={Number(ratingsData?.summary?.averageRating || 0)} readOnly precision={0.1} />
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="overline" color="text.secondary">Evaluations</Typography>
                            <Typography variant="h4" fontWeight={800}>{Number(ratingsData?.summary?.evaluationCount || 0)}</Typography>
                            <Typography variant="caption" color="text.secondary">Submitted: {Number(ratingsData?.summary?.submittedCount || 0)}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="overline" color="text.secondary">Last Evaluation</Typography>
                            <Typography variant="h6" fontWeight={700}>{ratingsData?.summary?.lastEvaluationDate || '—'}</Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Recent Evaluations</Typography>
                      {!Array.isArray(ratingsData?.evaluations) || ratingsData.evaluations.length === 0 ? (
                        <Alert severity="info">No evaluations yet for this staff member in the current context.</Alert>
                      ) : (
                        <List dense>
                          {ratingsData.evaluations.map((ev) => (
                            <ListItem key={ev.id} divider>
                              <ListItemText
                                primary={`${ev.evaluationDate || '—'}${ev.period ? ` • ${ev.period}` : ''}`}
                                secondary={`Status: ${ev.status} • Rating: ${(Number(ev.overallRating || 0)).toFixed(1)}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </>
                  )}
                </Box>
              )}
              
              {tabValue === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('users.permissions.title')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('users.permissions.rolePermissions') || 'Role Permissions'}</Typography>
                      <List dense>
                        {(permData?.rolePermissions || []).map((p) => (
                          <ListItem key={`role-${p}`}>
                            <ListItemText primary={p} secondary={t('users.permissions.allowed')} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('users.permissions.effective') || 'Effective Permissions'}</Typography>
                      <List dense>
                        {(permData?.effectivePermissions || []).map((p) => (
                          <ListItem key={`eff-${p}`}>
                            <ListItemText primary={p} secondary={t('users.permissions.allowed')} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('users.permissions.overrides') || 'Overrides'}</Typography>
                      {!permData?.overrides && (
                        <Typography variant="body2" color="text.secondary">{t('users.permissions.noOverrides') || 'No overrides set'}</Typography>
                      )}
                      {permData?.overrides && (
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">{t('users.permissions.allowOverrides') || 'Allow'}</Typography>
                            <List dense>
                              {(permData?.overrides?.allow || []).map((p) => (
                                <ListItem key={`allow-${p}`}>
                                  <ListItemText primary={p} secondary={t('users.permissions.allowed')} />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">{t('users.permissions.denyOverrides') || 'Deny'}</Typography>
                            <List dense>
                              {(permData?.overrides?.deny || []).map((p) => (
                                <ListItem key={`deny-${p}`}>
                                  <ListItemText primary={p} secondary={t('users.permissions.denied') || 'Denied'} />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
    </Container>
  );
};

export default UserDetailsPage;